import { Box } from "@chakra-ui/layout"
import React from "react"
import styled from "styled-components"

import FunctionLink from "~/components/atoms/FunctionLink"
import Icon from "~/components/atoms/Icon"
import Typography from "~/components/atoms/Typography"
import ActionPairButton, { Action, MainAction } from "~/components/molecules/ActionPairButton"
import { Color, RadiusSize, Shadow, SpacingSize, Z } from "~/consts/style-mixins"

type ExtendMainAction = MainAction & {
  only?: boolean
}

interface Props {
  open: boolean
  heading?: string | JSX.Element
  className?: string
  popupAreaClassName?: string
  mainAction?: ExtendMainAction
  subAction?: Action
  thirdAction?: Action
  vertical?: boolean
  onClickClose?: () => void
  overlayAction?: () => void
}

const Dialog: React.FC<Props> = (props) => {
  const {
    open,
    heading,
    mainAction,
    subAction,
    thirdAction,
    children,
    className,
    popupAreaClassName,
    vertical,
    onClickClose,
    overlayAction,
  } = props

  return (
    <StyledDialogWrapper open={open} className={className}>
      <StyledDialogOverlay onClick={overlayAction} />

      <StyledDialog>
        <div className={popupAreaClassName}>
          <div className="dialog-inner">
            {onClickClose && (
              <Box position="absolute" top={18} right={18} cursor="pointer" onClick={onClickClose}>
                <Icon type="Cancel" size={15} />
              </Box>
            )}
            <div>
              {heading && (
                <Box mb={SpacingSize._3XS}>
                  <Typography type="Heading" colorProp={Color.Black}>
                    {heading}
                  </Typography>
                </Box>
              )}
              <Typography type="Note" colorProp={Color.Gray}>
                {children}
              </Typography>
            </div>

            {!mainAction ? (
              <></>
            ) : mainAction.only ? (
              <StyledCenter>
                <FunctionLink size="Medium" onClick={mainAction.action}>
                  {mainAction.label}
                </FunctionLink>
              </StyledCenter>
            ) : (
              <StyledActionPairButtonWrapper hasSub={subAction} hasThird={thirdAction} vertical={vertical}>
                <ActionPairButton
                  mainAction={mainAction}
                  subAction={subAction}
                  thirdAction={thirdAction}
                  vertical={vertical}
                />
              </StyledActionPairButtonWrapper>
            )}
          </div>
        </div>
      </StyledDialog>
    </StyledDialogWrapper>
  )
}

interface StyledProps {
  open?: boolean
  hasThird?: Action
  hasSub?: Action
  vertical?: boolean
}

// NOTE: -webkit-fill-available はiPhoneで高さ100%を表現する
const StyledDialogWrapper = styled.div<StyledProps>`
  position: fixed;
  z-index: ${Z.Modal};
  padding: 0 ${SpacingSize.M}px;
  top: 0;
  left: 0;
  width: 100vw;
  height: ${() => {
    if (typeof window === "undefined") return "100vh"
    return window.navigator.userAgent.includes("iPhone") ? "-webkit-fill-available" : "100vh"
  }};
  overflow: scroll;
  display: ${({ open }) => (open ? "flex" : "none")};
  place-items: center;
`

const StyledDialog = styled.div`
  width: 100%;
  max-width: 400px;
  margin: auto;
  .dialog-inner {
    padding: ${SpacingSize.XL}px;
    background-color: ${Color.White};
    border-radius: ${RadiusSize.Medium}px;
    ${Shadow.Deep};
    position: relative;
  }
  z-index: 1;
`

const StyledDialogOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  /* NOTE: 100% だとなぜか端に隙間ができるので 101% にしている */
  width: 101%;
  height: 101%;
  background: black;
  opacity: 0.6;
`

const StyledActionPairButtonWrapper = styled.div`
  margin-top: ${SpacingSize.M}px;
  width: 100%;
  ${({ vertical, hasSub }: StyledProps) =>
    vertical ? "margin: auto" : hasSub ? "margin-left: auto; margin-right: auto;" : "margin-left: auto;"}
`

const StyledCenter = styled.div`
  margin-top: ${SpacingSize.M}px;
  display: grid;
  place-content: center;
`

export default Dialog
